/**
 * TodayPage — Vandaag (spec §8.1).
 *
 * Content order (fixed by the spec):
 * 1. time-aware Dutch greeting + formatted date
 * 2. cloud/offline state when relevant (pending writes, legacy local data)
 * 3. calm daily focus card (deterministic by date, no health claims)
 * 4. next Planner items (or a clear 'Plan je dag' CTA on an empty day)
 * 5. medication due summary when schedules produce occurrences today
 * 6. pinned Onthouden items
 * 7. six module shortcuts
 * 8. caregiver messages for the client when permitted
 *
 * Every repository source fails independently: SUPABASE_NOT_CONFIGURED shows a
 * calm 'cloud niet ingesteld' mini-state per module and the page stays
 * coherent. Data refreshes when the page regains focus/visibility.
 */
import { useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Banner, Button, Card, EmptyState, FeatureCard, Skeleton } from '@/components/ui'
import {
  BellIcon,
  BreathIcon,
  CloudIcon,
  HeartIcon,
  JournalIcon,
  MapPinIcon,
  MoonIcon,
  PillIcon,
  PinIcon,
  PlannerIcon,
  PlusIcon,
  RememberIcon,
} from '@/components/icons'
import { useAuth } from '@/features/auth'
import { MedicationCard } from '@/features/medication'
import { PATHS } from '@/app/router/paths'
import type { AppNotification, Note, PlannerItem } from '@/types'
import { focusForDate } from './focus'
import {
  firstNameOf,
  formatDutchDate,
  formatMinutesOfDay,
  formatShortDutchDateTime,
  greetingForHour,
  toLocalISODate,
} from './todayText'
import { pickUpcomingPlannerItems, useTodayData, type SectionState } from './useTodayData'
import './today.css'

const MODULE_SHORTCUTS = [
  { to: PATHS.app.remember, title: 'Onthouden', description: 'Notities en herinneringen', icon: <RememberIcon size={22} /> },
  { to: PATHS.app.planner, title: 'Planner', description: 'Je dag in blokken', icon: <PlannerIcon size={22} /> },
  { to: PATHS.app.breathing, title: 'Ademhaling', description: 'Rustige oefeningen', icon: <BreathIcon size={22} /> },
  { to: PATHS.app.journal, title: 'Dagboek', description: 'Je dag terugkijken', icon: <JournalIcon size={22} /> },
  { to: PATHS.app.health, title: 'Gezondheid', description: 'Slaap, water en schema’s', icon: <HeartIcon size={22} /> },
  { to: PATHS.app.places, title: 'Plaatsen', description: 'Waar liggen je spullen', icon: <MapPinIcon size={22} /> },
] as const

/** Calm mini-state shown when the cloud is not configured for this module. */
function CloudNotConfiguredNote() {
  return (
    <p className="today-cloud-note">
      <CloudIcon size={16} />
      <span>Cloud niet ingesteld. Deze gegevens kunnen nu niet worden opgehaald.</span>
    </p>
  )
}

function SectionErrorNote({ onRetry, retrying }: { onRetry: () => void; retrying: boolean }) {
  return (
    <div className="today-error-note" role="status">
      <span>Dit onderdeel kon niet laden.</span>
      <Button variant="ghost" size="sm" onClick={onRetry} disabled={retrying} loading={retrying}>
        Opnieuw proberen
      </Button>
    </div>
  )
}

function PlannerSection({ state, onRetry, retrying }: { state: SectionState<PlannerItem[]>; onRetry: () => void; retrying: boolean }) {
  const navigate = useNavigate()
  const upcoming = useMemo(() => pickUpcomingPlannerItems(state.data, currentMinute()), [state.data])

  return (
    <section className="today-section" aria-labelledby="today-planner-heading">
      <div className="today-section-header">
        <h2 id="today-planner-heading" className="today-section-title">
          Vandaag in je planner
        </h2>
        <Link className="today-section-link" to={PATHS.app.planner}>
          Alles bekijken
        </Link>
      </div>

      {state.status === 'loading' ? <Skeleton lines={2} /> : null}
      {state.status === 'unavailable' ? <CloudNotConfiguredNote /> : null}
      {state.status === 'error' ? <SectionErrorNote onRetry={onRetry} retrying={retrying} /> : null}

      {state.status === 'ready' && state.data.length === 0 ? (
        <EmptyState
          icon={<PlannerIcon size={28} />}
          title="Nog niets gepland"
          body="Maak een rustig plan voor je dag, blok voor blok."
          action={
            <Button variant="primary" onClick={() => navigate(PATHS.app.planner)}>
              <PlusIcon size={18} />
              Plan je dag
            </Button>
          }
        />
      ) : null}

      {state.status === 'ready' && state.data.length > 0 ? (
        upcoming.length > 0 ? (
          <Card padding="sm" className="today-list-card">
            <ul className="today-item-list">
              {upcoming.map((item) => (
                <li key={item.id}>
                  <Link className="today-item-row" to={PATHS.app.planner}>
                    <span className="today-item-color" style={{ backgroundColor: item.color }} aria-hidden="true" />
                    <span className="today-item-time">
                      {formatMinutesOfDay(item.startMinute)} – {formatMinutesOfDay(item.startMinute + item.durationMinutes)}
                    </span>
                    <span className="today-item-title">{item.activityName}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </Card>
        ) : (
          <Card padding="sm" className="today-list-card">
            <p className="today-muted">
              Alles van vandaag is geweest.{' '}
              <Link className="today-inline-link" to={PATHS.app.planner}>
                Bekijk je planner
              </Link>{' '}
              voor morgen.
            </p>
          </Card>
        )
      ) : null}
    </section>
  )
}

function currentMinute(): number {
  const now = new Date()
  return now.getHours() * 60 + now.getMinutes()
}

function PinnedNotesSection({ state, onRetry, retrying }: { state: SectionState<Note[]>; onRetry: () => void; retrying: boolean }) {
  return (
    <section className="today-section" aria-labelledby="today-notes-heading">
      <div className="today-section-header">
        <h2 id="today-notes-heading" className="today-section-title">
          Vastgezet in Onthouden
        </h2>
        <Link className="today-section-link" to={PATHS.app.remember}>
          Alles bekijken
        </Link>
      </div>

      {state.status === 'loading' ? <Skeleton lines={2} /> : null}
      {state.status === 'unavailable' ? <CloudNotConfiguredNote /> : null}
      {state.status === 'error' ? <SectionErrorNote onRetry={onRetry} retrying={retrying} /> : null}

      {state.status === 'ready' && state.data.length > 0 ? (
        <Card padding="sm" className="today-list-card">
          <ul className="today-item-list">
            {state.data.map((note) => (
              <li key={note.id}>
                <Link className="today-item-row" to={PATHS.app.remember}>
                  <span className="today-item-pin" aria-hidden="true">
                    <PinIcon size={16} />
                  </span>
                  <span className="today-item-title">{note.title}</span>
                  <span className="today-item-meta">{note.category}</span>
                </Link>
              </li>
            ))}
          </ul>
        </Card>
      ) : null}
    </section>
  )
}

function CaregiverMessagesSection({ messages }: { messages: AppNotification[] }) {
  return (
    <section className="today-section" aria-labelledby="today-caregiver-heading">
      <div className="today-section-header">
        <h2 id="today-caregiver-heading" className="today-section-title">
          Berichten van je begeleider
        </h2>
        <Link className="today-section-link" to={PATHS.app.notifications}>
          Alle meldingen
        </Link>
      </div>
      <div className="today-message-list">
        {messages.map((message) => {
          const inner = (
            <>
              <span className="today-message-icon" aria-hidden="true">
                <BellIcon size={18} />
              </span>
              <span className="today-message-text">
                <span className="today-message-title">{message.title}</span>
                {message.body ? <span className="today-message-body">{message.body}</span> : null}
                <span className="today-item-meta">{formatShortDutchDateTime(message.createdAt)}</span>
              </span>
            </>
          )
          return message.deepLink ? (
            <Link key={message.id} className="today-message" to={message.deepLink}>
              {inner}
            </Link>
          ) : (
            <Card key={message.id} padding="sm" className="today-message today-message--static">
              {inner}
            </Card>
          )
        })}
      </div>
    </section>
  )
}

export default function TodayPage() {
  const { profile, role } = useAuth()
  const navigate = useNavigate()
  const {
    online,
    pendingWrites,
    legacySummary,
    planner,
    pinnedNotes,
    medicationOccurrences,
    caregiverMessages,
    retry,
  } = useTodayData()
  const [retrying, setRetrying] = useState(false)

  const now = new Date()
  const todayISO = toLocalISODate(now)
  const greeting = greetingForHour(now.getHours())
  const firstName = firstNameOf(profile?.displayName)
  const focus = focusForDate(todayISO)

  const legacyTotal = legacySummary
    ? legacySummary.plannerItems +
      legacySummary.notes +
      legacySummary.places +
      legacySummary.journalEntries +
      legacySummary.healthItems +
      legacySummary.breathingSessions
    : 0

  const showMedication = medicationOccurrences.status === 'ready' && medicationOccurrences.data.length > 0
  const showCaregiverMessages =
    role === 'client' && caregiverMessages.status === 'ready' && caregiverMessages.data.length > 0

  const handleRetry = () => {
    if (retrying) return
    setRetrying(true)
    void retry().finally(() => setRetrying(false))
  }

  return (
    <div className="today-page">
      {/* 1. Date + time-aware greeting */}
      <header className="today-header">
        <p className="today-date">{formatDutchDate(now)}</p>
        <h1 className="today-greeting">
          {greeting}
          {firstName ? `, ${firstName}` : ''}
        </h1>
      </header>

      {/* 2. Cloud/offline state when relevant (OfflineBanner lives in AppShell) */}
      {!online ? (
        <Banner tone="warning">
          Je bent offline. Getoonde gegevens kunnen verouderd zijn en wijzigingen synchroniseren
          zodra je weer online bent.
        </Banner>
      ) : null}
      {pendingWrites > 0 ? (
        <Banner tone="info">
          {pendingWrites === 1
            ? '1 wijziging wacht op synchronisatie.'
            : `${pendingWrites} wijzigingen wachten op synchronisatie.`}
        </Banner>
      ) : null}
      {legacySummary && legacyTotal > 0 ? (
        <Banner
          tone="info"
          action={
            <Button variant="secondary" size="sm" onClick={() => navigate(PATHS.app.data)}>
              Bekijken
            </Button>
          }
        >
          Er staan nog gegevens van de vorige versie van RUST op dit apparaat ({legacyTotal}{' '}
          {legacyTotal === 1 ? 'item' : 'items'}). Je kunt ze importeren naar de cloud.
        </Banner>
      ) : null}

      {/* 3. Calm daily focus card */}
      <Card padding="md" className="today-focus-card">
        <div className="today-focus-header">
          <span className="today-focus-icon" aria-hidden="true">
            <MoonIcon size={20} />
          </span>
          <h2 className="today-section-title">Focus voor vandaag</h2>
        </div>
        <p className="today-focus-text">{focus}</p>
      </Card>

      {/* 4. Next Planner items */}
      <PlannerSection state={planner} onRetry={handleRetry} retrying={retrying} />

      {/* 5. Medication due summary when schedules produce occurrences today */}
      {showMedication ? (
        <section className="today-section" aria-labelledby="today-medication-heading">
          <div className="today-section-header">
            <h2 id="today-medication-heading" className="today-section-title">
              <span className="today-section-icon" aria-hidden="true">
                <PillIcon size={18} />
              </span>
              Medicatie vandaag
            </h2>
          </div>
          <MedicationCard date={todayISO} />
        </section>
      ) : null}
      {medicationOccurrences.status === 'unavailable' ? (
        <section className="today-section" aria-label="Medicatie vandaag">
          <CloudNotConfiguredNote />
        </section>
      ) : null}

      {/* 6. Pinned Onthouden items */}
      <PinnedNotesSection state={pinnedNotes} onRetry={handleRetry} retrying={retrying} />

      {/* 7. Six module shortcuts */}
      <section className="today-section" aria-labelledby="today-shortcuts-heading">
        <h2 id="today-shortcuts-heading" className="today-section-title">
          Snel naar
        </h2>
        <div className="today-shortcut-grid">
          {MODULE_SHORTCUTS.map((shortcut) => (
            <FeatureCard
              key={shortcut.to}
              to={shortcut.to}
              icon={shortcut.icon}
              title={shortcut.title}
              description={shortcut.description}
            />
          ))}
        </div>
      </section>

      {/* 8. Caregiver messages for the client when permitted */}
      {showCaregiverMessages ? <CaregiverMessagesSection messages={caregiverMessages.data} /> : null}

      {pinnedNotes.status === 'ready' && pinnedNotes.data.length === 0 ? (
        <p className="today-muted today-footnote">
          Tip: zet een notitie vast in{' '}
          <Link className="today-inline-link" to={PATHS.app.remember}>
            Onthouden
          </Link>{' '}
          om hem hier te zien.
        </p>
      ) : null}
    </div>
  )
}
